import React, { PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import { QueryBuilder, QueryBuilderRenderProps, QueryBuilderState } from "@cubejs-client/react";
import cubejs, { CubeApi, DryRunResponse, Query } from "@cubejs-client/core";
import { useParsed } from "@refinedev/core";
import { config } from "@src/config";
import { IProduct } from "@src/types/user";
import { DEFAULT_QUERY_BUILDER } from "@src/config/query-builder";
import { useAppContext } from "./app-context";
import { useGetExplorations } from "@src/hooks/use-get-explorations";
import { getModeOfLocation } from "@src/util/fb-exploration/get-mode-of-location";
import { LoadingFullScreen } from "@src/components/loading/loading-full-screen";
import { Form, Result, Spin } from "antd";
import { uniqBy } from "lodash";
import { FormInstance } from "antd/lib/form";
import { getDateRangeWithTime } from "@src/util/time-form";
import { shared } from "@src/util/shared";

export const ExploreContext = createContext({} as QueryBuilderRenderProps & ExploreContextProviderProps);

type IProps = {
  children: React.ReactNode;
};
interface QueryBuilderRenderInternalProps {
  restProps: QueryBuilderRenderProps;
}
interface ManualFetchDryRunParams {
  query: Query;
  onSuccess?: (res?: DryRunResponse) => void;
  onError?: (err?: Error) => void;
  onSetters?: () => void;
}

interface ExploreContextProviderProps {
  setProduct: React.Dispatch<React.SetStateAction<IProduct | undefined>>;
  product?: IProduct;
  cubeToken?: string;
  manualFetchDryRun: (params: ManualFetchDryRunParams) => Promise<void>;
  form?: FormInstance<any>;
  cubeApi?: CubeApi;
  exploration: any;
  isLoading?: boolean;
}
const ExploreProvider: React.FC<PropsWithChildren<ExploreContextProviderProps & QueryBuilderRenderInternalProps>> = ({
  restProps,
  setProduct,
  product,
  children,
  cubeToken,
  manualFetchDryRun,
  cubeApi,
  form,
  exploration,
  isLoading,
}) => {
  const value = React.useMemo(() => {
    return { ...restProps, setProduct, product, cubeToken, manualFetchDryRun, form, cubeApi, exploration, isLoading };
  }, [restProps, setProduct, product, cubeToken, manualFetchDryRun, form, cubeApi, isLoading, exploration]);
  return <ExploreContext.Provider value={value}>{children}</ExploreContext.Provider>;
};

export const ExploreContextProvider: React.FC<IProps> = ({ children }) => {
  const { id } = useParsed();
  const { isEditMode, isViewMode } = getModeOfLocation();
  const [form] = Form.useForm();

  const isCreateMode = !isEditMode && !isViewMode;

  const [product, setProduct] = React.useState<IProduct>();
  const [dryRunError, setDryRunError] = React.useState<Error | null>(null);
  const productCode = isCreateMode ? product?.productCode ?? config.DEFAULT_PRODUCT_CODE : product?.productCode;
  const { products } = useAppContext();
  const { exploration, isLoading: isLoadingGetInitValue, error } = useGetExplorations();

  React.useEffect(() => {
    if (isLoadingGetInitValue) return;
    if (!exploration) return;
    if (products.length === 0) return;

    const product = products.find((product) => product.productCode === exploration?.vizState.product_code);
    if (product) {
      setProduct(product);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(exploration), isLoadingGetInitValue, JSON.stringify(products)]);

  const CUBE_TOKEN = `Bearer ${shared.get("token")}`;
  const isHasConfigExplore = isEditMode || isViewMode;

  const isLoadingInitConfig = isHasConfigExplore && isLoadingGetInitValue;
  const isLoading = isLoadingInitConfig;

  const isShowSpinner = !productCode || (!!productCode && isLoading);
  const cubejsApi = cubejs(CUBE_TOKEN, {
    apiUrl: config.NESTJS_URL + "/api/proxy/cube",
    headers: {
      "product-code": productCode!,
    },
  });

  const manualFetchDryRun = async ({ query, onSuccess, onError, onSetters }: ManualFetchDryRunParams) => {
    await cubejsApi
      .dryRun(query)
      .then((res) => {
        setDryRunError(null);
        onSuccess?.(res);
      })
      .catch((error) => {
        setDryRunError(error);
        onError?.(error);
      })
      .finally(() => {
        onSetters?.();
      });
  };

  const defaultQuery: any = isHasConfigExplore ? exploration?.vizState.query : DEFAULT_QUERY_BUILDER;
  const queryBuilderKey = isHasConfigExplore ? (productCode || config.DEFAULT_PRODUCT_CODE) + id : productCode;

  const onStateChangeHeuristics = (_: QueryBuilderState, newState: QueryBuilderState) => {
    const customTimeRange = form.getFieldValue("custom-time-range");
    return {
      ...newState,
      query: {
        ...newState.query,
        timeDimensions: uniqBy(
          [
            {
              dimension: config.APP_REPORT_DATE,
              dateRange: getDateRangeWithTime("custom", customTimeRange) ?? "Last 7 days",
            },
            ...(newState.query?.timeDimensions ?? [])
              .filter((item) => !!item.granularity)
              .map((item) => {
                delete item.dateRange;
                return item;
              }),
          ],
          (item) => item.dimension,
        ),
      },
    } as QueryBuilderState;
  };

  const errorStatus = error?.statusCode ?? error?.response?.status;

  if (errorStatus === 403) {
    return <Result status="403" title="403" subTitle="Sorry, you are not authorized to access this page." />;
  }
  if (errorStatus === 404 || errorStatus === 500) {
    return <Result status="404" title="404" subTitle="Exploration not found" />;
  }
  return (
    <Spin spinning={isShowSpinner}>
      {(exploration && productCode) || isCreateMode ? (
        <QueryBuilder
          key={queryBuilderKey}
          cubeApi={cubejsApi}
          defaultChartType="table"
          disableHeuristics={false}
          stateChangeHeuristics={onStateChangeHeuristics}
          initialVizState={exploration?.vizState}
          defaultQuery={defaultQuery}
          wrapWithQueryRenderer={false}
          render={(restProps) => {
            return (
              <ExploreProvider
                form={form}
                setProduct={setProduct}
                cubeApi={cubejsApi}
                restProps={{
                  ...restProps,
                  error: restProps.error ?? dryRunError,
                }}
                product={product}
                cubeToken={CUBE_TOKEN}
                manualFetchDryRun={manualFetchDryRun}
                exploration={exploration}
                isLoading={isLoading}
              >
                {children}
              </ExploreProvider>
            );
          }}
        />
      ) : (
        <LoadingFullScreen />
      )}
    </Spin>
  );
};

export const useExploreContext = () => useContext(ExploreContext);
